import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { apiUrl } from '@/lib/api'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Badge } from '@shared/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@shared/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faPlus, 
  faEye,
  faCheck,
  faTimes,
  faUsers
} from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import { toast } from '@shared/hooks/use-toast'
import { PageHeader } from '@shared/components/ui/page-header'

interface Tenant {
  id: string
  name: string
  slug: string
  status: 'active' | 'suspended'
  created_at: string
  plan_name?: string
}

interface CreateTenantData {
  name: string
  slug: string
  planId?: string
}

// API functions
const fetchTenants = async (): Promise<Tenant[]> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  if (!idToken) throw new Error('No authentication token found')
  
  const response = await fetch(apiUrl('/api/admin/tenants'), {
    headers: {
      'Authorization': `Bearer ${idToken}`
    }
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch tenants')
  }
  
  const data = await response.json()
  return data.tenants || []
}

const createTenant = async (tenantData: CreateTenantData): Promise<Tenant> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  if (!idToken) throw new Error('No authentication token found')
  
  const response = await fetch(apiUrl('/api/admin/tenants'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify(tenantData)
  })
  
  if (!response.ok) {
    throw new Error('Failed to create tenant')
  }
  
  return response.json()
}

const updateTenantStatus = async ({ id, status }: { id: string; status: 'active' | 'suspended' }): Promise<void> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  if (!idToken) throw new Error('No authentication token found')
  
  const response = await fetch(apiUrl(`/api/admin/tenants/${id}/status`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({ status })
  })
  
  if (!response.ok) {
    throw new Error('Failed to update tenant status')
  }
}

const TenantsPage: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newTenant, setNewTenant] = useState<CreateTenantData>({ name: '', slug: '' })
  const queryClient = useQueryClient()

  // Queries
  const { data: tenants = [], isLoading, error } = useQuery({
    queryKey: ['tenants'],
    queryFn: fetchTenants,
    retry: false
  })

  // Mutations
  const createTenantMutation = useMutation({
    mutationFn: createTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      setIsCreateDialogOpen(false)
      setNewTenant({ name: '', slug: '' })
      toast({
        title: 'Success',
        description: 'Tenant created successfully',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  })

  const updateStatusMutation = useMutation({
    mutationFn: updateTenantStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast({
        title: 'Success',
        description: 'Tenant status updated successfully',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  })

  const handleCreateTenant = () => {
    if (!newTenant.name || !newTenant.slug) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }
    createTenantMutation.mutate(newTenant)
  }

  const handleStatusToggle = (tenant: Tenant) => {
    const newStatus = tenant.status === 'active' ? 'suspended' : 'active'
    updateStatusMutation.mutate({ id: tenant.id, status: newStatus })
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Tenants" description="Manage platform tenants and their status" />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-red-500">Error: {error.message}</p>
              <p className="text-muted-foreground mt-2">
                Please check your API key and try again
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Tenants" description="Manage platform tenants and their status">
        <div className="flex items-center gap-2">
          <Button variant="default" onClick={() => setIsCreateDialogOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
            Create Tenant
          </Button>
        </div>
      </PageHeader>

      <div className="flex justify-between items-center">
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>
                Add a new tenant to the platform. The slug will be used for the subdomain.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Tenant Name</Label>
                <Input
                  id="name"
                  value={newTenant.name}
                  onChange={(e) => {
                    const name = e.target.value
                    setNewTenant({ ...newTenant, name, slug: generateSlug(name) })
                  }}
                  placeholder="Enter tenant name"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={newTenant.slug}
                  onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
                  placeholder="tenant-slug"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This will be used for the subdomain: {newTenant.slug}.yourdomain.com
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTenant}
                disabled={createTenantMutation.isPending}
              >
                {createTenantMutation.isPending ? 'Creating...' : 'Create Tenant'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenants.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {tenants.filter(t => t.status === 'active').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suspended</CardTitle>
            <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {tenants.filter(t => t.status === 'suspended').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tenants</CardTitle>
          <CardDescription>
            A list of all tenants on the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading tenants...</p>
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No tenants found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell className="font-mono text-sm">{tenant.slug}</TableCell>
                    <TableCell>
                      <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                        {tenant.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{tenant.plan_name || 'No Plan'}</TableCell>
                    <TableCell>
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {/*<Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusToggle(tenant)}
                          disabled={updateStatusMutation.isPending}
                        >
                          {tenant.status === 'active' ? 'Suspend' : 'Activate'}
                        </Button>*/}
                        <Link to={`/tenants/${tenant.id}`}>
                          <Button variant="outline" size="sm"><FontAwesomeIcon icon={faEye} className="h-4 w-4" />View</Button>
                        </Link>
                        {/*<Link to={`/tenants/${tenant.id}/users`}>
                          <Button variant="outline" size="sm">Users</Button>
                        </Link>
                        <Link to={`/tenants/${tenant.id}/roles`}>
                          <Button variant="outline" size="sm">Roles</Button>
                        </Link>
                        <Link to={`/tenants/${tenant.id}/plan-features`}>
                          <Button variant="outline" size="sm">Features</Button>
                        </Link>*/}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default TenantsPage
